/**
 * Frontier Defence Lab — five attack-vector / defensive-engineering tracks
 * for frontier-model deployment.
 *
 * Tracks:
 *   - jailbreak     · prompt-injection, model bypasses, training poisoning
 *   - sandbox       · isolation for model-generated code (gVisor, Firecracker, e2b)
 *   - enclave       · TEEs / confidential compute / attestation
 *   - digital_twin  · simulation environments to test AI before production
 *   - patch_cycle   · vendor patch cadence vs exploit availability
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Shield, Boxes, Lock, Activity, Clock, RefreshCw, Github, ExternalLink, Newspaper,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Repo { label: string; url: string }
interface Track {
  id: string
  label: string
  description: string
  keyword_count: number
  repos: Repo[]
}
interface Article {
  id: number
  title: string
  summary: string
  source: string
  url?: string | null
  category: string
  region: string
  published_at: string | null
}

const TRACK_ICONS: Record<string, any> = {
  jailbreak:    Shield,
  sandbox:      Boxes,
  enclave:      Lock,
  digital_twin: Activity,
  patch_cycle:  Clock,
}
const TRACK_TONES: Record<string, string> = {
  jailbreak:    'rose',
  sandbox:      'amber',
  enclave:      'emerald',
  digital_twin: 'blue',
  patch_cycle:  'purple',
}

export default function FrontierDefencePage() {
  const [tracks, setTracks] = useState<Track[]>([])
  const [byTrack, setByTrack] = useState<Record<string, Article[]>>({})
  const [counts, setCounts] = useState<Record<string, number>>({})
  const [active, setActive] = useState<string>('jailbreak')
  const [loading, setLoading] = useState(false)

  const refresh = () => {
    setLoading(true)
    Promise.all([
      api.get('/api/frontier/defence/tracks').then(r => setTracks(r.data?.tracks || [])).catch(() => {}),
      api.get('/api/frontier/defence/news', { params: { limit: 40, days: 60 } })
        .then(r => {
          setByTrack(r.data?.results || {})
          setCounts(r.data?.counts || {})
        })
        .catch(() => {}),
    ]).finally(() => setLoading(false))
  }
  useEffect(() => { refresh() }, [])

  const activeTrack = useMemo(() => tracks.find(t => t.id === active), [tracks, active])
  const activeArticles = byTrack[active] || []

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Shield size={18} className="text-rose" /> FRONTIER DEFENCE LAB
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          jailbreaks · sandboxes · enclaves · twins · patch cycle
        </span>
        <button type="button" onClick={refresh} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      {/* Track tabs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {tracks.map(t => {
          const Icon = TRACK_ICONS[t.id] || Shield
          const tone = TRACK_TONES[t.id] || 'rose'
          const isActive = active === t.id
          const cnt = counts[t.id] ?? 0
          const colour =
            tone === 'rose'    ? 'border-rose/40 text-rose-light'    :
            tone === 'amber'   ? 'border-amber/40 text-amber-light'  :
            tone === 'emerald' ? 'border-emerald/40 text-emerald'    :
            tone === 'blue'    ? 'border-blue/40 text-blue-light'    :
                                  'border-purple/40 text-purple-light'
          return (
            <button key={t.id} type="button" onClick={() => setActive(t.id)}
              className={clsx(
                'card p-3 text-left transition-colors border',
                isActive ? colour + ' ring-1 ring-current' : 'border-border hover:border-text-muted',
              )}>
              <div className="flex items-center gap-2">
                <Icon size={14} className={isActive ? '' : 'text-text-muted'} />
                <span className="text-xs font-semibold">{t.label.split('·')[0].trim()}</span>
                <span className="ml-auto text-[10px] font-mono text-text-muted">{cnt}</span>
              </div>
              <div className="text-[10px] text-text-muted mt-1 line-clamp-2">{t.label}</div>
            </button>
          )
        })}
      </div>

      {/* Active track detail */}
      {activeTrack && (
        <>
          <section className="card p-4">
            <h2 className="text-sm font-semibold text-text-primary mb-1">{activeTrack.label}</h2>
            <p className="text-xs text-text-secondary leading-relaxed">{activeTrack.description}</p>
            <div className="text-[10px] text-text-muted mt-2 font-mono">
              {activeTrack.keyword_count} keywords · {(byTrack[active] || []).length} articles in last 60 days
            </div>
          </section>

          <section className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            {/* Articles */}
            <div className="card p-0 lg:col-span-2">
              <header className="p-3 border-b border-border flex items-center gap-2">
                <Newspaper size={14} className="text-amber-light" />
                <h3 className="text-sm font-semibold uppercase tracking-widest">Recent intelligence</h3>
                <span className="text-[10px] text-text-muted">last 60 days · sorted newest first</span>
              </header>
              <div className="max-h-[600px] overflow-auto">
                {activeArticles.length === 0 ? (
                  <div className="p-8 text-center text-text-muted italic text-xs">
                    No recent articles match this track. Tune keywords in <code className="font-mono">backend/api/frontier_defence.py</code>{' '}
                    or wait for the next ingest cycle.
                  </div>
                ) : (
                  <ul className="divide-y divide-border">
                    {activeArticles.map(a => (
                      <li key={a.id} className="p-3 hover:bg-surface-raised/40 transition-colors">
                        <div className="flex items-start gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="badge bg-surface-raised text-text-muted">{a.source}</span>
                              <span className="badge bg-blue/10 text-blue-light border border-blue/30">{a.category}</span>
                              {a.region && a.region !== 'Global' && (
                                <span className="badge bg-amber/10 text-amber-light">{a.region}</span>
                              )}
                            </div>
                            {a.url ? (
                              <a href={a.url} target="_blank" rel="noreferrer"
                                className="text-sm text-text-primary hover:text-blue-light hover:underline">
                                {a.title}
                              </a>
                            ) : (
                              <span className="text-sm text-text-primary">{a.title}</span>
                            )}
                            {a.summary && (
                              <p className="text-xs text-text-secondary mt-1 line-clamp-2">{a.summary}</p>
                            )}
                          </div>
                          <span className="text-[10px] text-text-muted font-mono shrink-0">
                            {a.published_at?.slice(0, 10)}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Curated repos / tools */}
            <div className="card p-0">
              <header className="p-3 border-b border-border flex items-center gap-2">
                <Github size={14} />
                <h3 className="text-sm font-semibold uppercase tracking-widest">Tools & repos</h3>
              </header>
              <ul className="p-3 space-y-2">
                {activeTrack.repos.map(r => (
                  <li key={r.url}>
                    <a href={r.url} target="_blank" rel="noreferrer"
                      className="block p-2 rounded border border-border hover:border-blue/40 hover:bg-surface-raised/40 transition-colors">
                      <div className="flex items-center gap-2 text-xs text-text-primary">
                        <Github size={11} className="text-text-muted shrink-0" />
                        <span className="font-medium truncate">{r.label}</span>
                        <ExternalLink size={10} className="ml-auto text-text-muted shrink-0" />
                      </div>
                      <div className="text-[10px] text-text-muted font-mono mt-0.5 truncate">{r.url.replace(/^https?:\/\//, '')}</div>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </>
      )}
    </div>
  )
}
